import React, { useMemo, useState, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router-dom'
import { FeatherLoader, useErrorService } from '@abroad/components'
import ExperienceBadge from './ExperienceBadge'
import InquiriesModal from './InquiriesModal'
import UserDetailsModal from './UserDetailsModal'
import API from '../../utils/API'
import { formatExperienceDate } from '../../utils/date'
import {
  getInquiryDisplayContact,
  getInquiryEnrollPrefill,
} from './inquiryContactUtils'

const formatOccupancyPreference = (pref) => {
  if (pref == null || String(pref).trim() === '') return '—'
  const raw = String(pref).trim()
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase()
}

const QuestsInquiries = ({ questDetail, onManageDataChange }) => {
  const { questId } = useParams()
  const Error = useErrorService()
  const [showModal, setShowModal] = useState(false)
  const [inquirieDetail, setInquirieDetail] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [questInquiries, setQuestInquiries] = useState([])
  const [showEnrollModal, setShowEnrollModal] = useState(false)
  const [enrollUserData, setEnrollUserData] = useState(null)

  const loadInquiries = async ({ refreshRail = false } = {}) => {
    setIsLoading(true)
    try {
      const { data } = await API.AdminAPI.quests.getInquirie(questId)
      const sortedInquiries = data.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      )
      const processedInquiries = sortedInquiries.map((inquiry) => {
        const { fullName, email } = getInquiryDisplayContact(inquiry)
        return {
          ...inquiry,
          fullName,
          email,
        }
      })
      setQuestInquiries(processedInquiries)
      setIsLoading(false)
      if (refreshRail && typeof onManageDataChange === 'function') {
        onManageDataChange()
      }
    } catch (e) {
      Error.showError(e)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadInquiries()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId])

  const inquiryStats = useMemo(() => {
    const total = questInquiries.length
    const enrolled = questInquiries.filter((q) => !!q?.isEnrolled).length
    return {
      total,
      enrolled,
      newCount: Math.max(0, total - enrolled),
    }
  }, [questInquiries])

  const handleOpenInquiry = (inquirie) => {
    setInquirieDetail(inquirie)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
  }

  const handleOpenEnroll = (inquirie) => {
    if (inquirie?.isEnrolled) return
    const { _id, id, ...inquiryData } = inquirie
    const { email, firstName, lastName } = getInquiryEnrollPrefill(inquirie)
    setEnrollUserData({
      ...inquiryData,
      email,
      firstName,
      lastName,
      readyToEnroll: !!(inquirie.readyToEnroll ?? inquirie.occupancyPreference),
      occupancyPreference: inquirie.occupancyPreference,
      inquiryId: _id || id,
    })
    setShowEnrollModal(true)
  }

  const handleCloseEnrollModal = () => {
    setShowEnrollModal(false)
    setEnrollUserData(null)
  }

  const handleEnrollSuccess = () => {
    loadInquiries({ refreshRail: true })
  }

  const handleRowKeyDown = (e, inquirie) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    if (e.target.closest('[data-inquiry-enroll]')) return
    e.preventDefault()
    handleOpenInquiry(inquirie)
  }

  return (
    <>
      <Helmet>
        <title>{`Experience-Inquiries | Abroad`}</title>
        <meta name='title' content={'Experience-Inquiries | Abroad'}></meta>
        <meta
          property='og:title'
          content={'Experience-Inquiries | Abroad'}></meta>
        <meta
          property='twitter:title'
          content={'Experience-Inquiries | Abroad'}></meta>
        <link
          rel='canonical'
          href={`${process.env.REACT_APP_DOMAIN}/admin/experiences/${questId}/inquiries`}></link>
        <meta
          property='og:url'
          content={`${process.env.REACT_APP_DOMAIN}/admin/experiences/${questId}/inquiries`}></meta>
        <meta
          property='twitter:url'
          content={`${process.env.REACT_APP_DOMAIN}/admin/experiences/${questId}/inquiries`}></meta>
      </Helmet>
      <div className='experience-manage-list'>
        <div className='experience-sumrow'>
          <div className='experience-stat'>
            <span>Inquiries</span>
            <b>{inquiryStats.total}</b>
            <small>this experience</small>
          </div>
          <div className='experience-stat'>
            <span>New</span>
            <b>{inquiryStats.newCount}</b>
            <small>not yet enrolled</small>
          </div>
          <div className='experience-stat'>
            <span>Enrolled</span>
            <b>{inquiryStats.enrolled}</b>
            <small>from inquiry</small>
          </div>
        </div>
        <div className='experience-tbl-wrap'>
          <table className='experience-tbl' style={{ minWidth: 760 }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Date</th>
                <th>Occupancy</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td className='text-center border-0 pt-3' colSpan='6'>
                    <FeatherLoader />
                  </td>
                </tr>
              )}
              {questInquiries.length === 0 && !isLoading && (
                <tr>
                  <td className='text-center s6 py-3' colSpan='6'>
                    No inquiries for this experience yet.
                  </td>
                </tr>
              )}
              {questInquiries?.length > 0 &&
                !isLoading &&
                questInquiries?.map((inquirie, index) => (
                  <tr
                    key={inquirie?._id || inquirie?.id || index}
                    className='experience-inquiry-row'
                    tabIndex={0}
                    onClick={() => handleOpenInquiry(inquirie)}
                    onKeyDown={(e) => handleRowKeyDown(e, inquirie)}>
                    <td className='experience-inquiry-name'>
                      {inquirie?.fullName}
                    </td>
                    <td className='text-lowercase'>{inquirie?.email}</td>
                    <td>{formatExperienceDate(inquirie?.createdAt) || '—'}</td>
                    <td>
                      {formatOccupancyPreference(inquirie?.occupancyPreference)}
                    </td>
                    <td>
                      {inquirie?.isEnrolled ? (
                        <ExperienceBadge title='Enrolled' variant='green' />
                      ) : (
                        <ExperienceBadge title='New' variant='amber' />
                      )}
                    </td>
                    <td
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}>
                      {inquirie?.isEnrolled ? (
                        <button
                          type='button'
                          disabled
                          className='experience-btn-sm'
                          data-inquiry-enroll>
                          Enrolled
                        </button>
                      ) : (
                        <button
                          type='button'
                          className='experience-btn-sm'
                          data-inquiry-enroll
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            handleOpenEnroll(inquirie)
                          }}>
                          Enroll
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <InquiriesModal
          showModal={showModal}
          handleClose={handleCloseModal}
          inquirieDetail={inquirieDetail}
          investments={questDetail?.investments}
          onEnroll={handleOpenEnroll}
        />
        {enrollUserData && (
          <UserDetailsModal
            showModal={showEnrollModal}
            handleClose={handleCloseEnrollModal}
            userData={enrollUserData}
            updateParticipants={handleEnrollSuccess}
            questDetails={questDetail}
            investments={questDetail?.investments}
            enrollFromInquiries
          />
        )}
      </div>
    </>
  )
}

export default QuestsInquiries
